import { useEffect, useId, useRef } from 'react'
import { Upload } from 'lucide-react'
import { uiScope } from '@/lib/cssScope'
import { Button } from '@/components/ui/Button'
import styles from '@/styles/components/ui/FilePicker.module.css'

export interface FilePickerProps {
  accept?: string
  value: File | null
  onChange: (file: File | null) => void
  chooseLabel: string
  noFileLabel: string
  disabled?: boolean
  className?: string
  /** If provided, run before opening the file dialog; return false to cancel (e.g. validation). */
  onBeforePick?: () => boolean
}

/**
 * Styled file selection: hidden native input + primary Button; shows selected file name.
 */
export function FilePicker({
  accept,
  value,
  onChange,
  chooseLabel,
  noFileLabel,
  disabled = false,
  className = '',
  onBeforePick,
}: FilePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const reactId = useId()

  useEffect(() => {
    if (value === null && inputRef.current) {
      inputRef.current.value = ''
    }
  }, [value])

  const inputId = `file-picker-${reactId.replace(/:/g, '')}`

  return (
    <div className={`${uiScope('file-picker')} ${styles.root} ${className}`.trim()}>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        className={styles.input}
        accept={accept}
        disabled={disabled}
        tabIndex={-1}
        aria-label={chooseLabel}
        onChange={(e) => {
          const next = e.target.files?.[0] ?? null
          onChange(next)
        }}
      />
      <div className={styles.row}>
        <Button
          type="button"
          color="primary"
          size="small"
          disabled={disabled}
          icon={<Upload className="h-4 w-4" aria-hidden />}
          onClick={() => {
            if (onBeforePick && !onBeforePick()) return
            inputRef.current?.click()
          }}
        >
          {chooseLabel}
        </Button>
        <span className={styles.fileName} title={value?.name}>
          {value?.name ?? noFileLabel}
        </span>
      </div>
    </div>
  )
}
